import { FC, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

export interface RadioOption {
  value: string;
  label: string;
  jsxLabel?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  needToTranslate?: boolean;
  legend?: ReactNode;
  name?: string;
  variant?: 'standard' | 'cards';
  required?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  error?: string;
  bare?: boolean;
}

const RadioGroup: FC<RadioGroupProps> = ({
  options,
  value,
  onChange,
  className = '',
  needToTranslate = false,
  legend,
  name,
  variant = 'standard',
  required = false,
  disabled = false,
  hint,
  error,
  bare = true,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const description =
    [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <fieldset
      className={[
        'kit-selection',
        'kit-radio-group',
        bare || !legend ? 'kit-radio-group--bare' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
      aria-describedby={description}
    >
      <legend>
        {legend || 'Options'}
        {required && legend ? <span aria-hidden="true"> *</span> : null}
      </legend>
      {hint && <p id={`${id}-hint`}>{hint}</p>}
      <div className={variant === 'cards' ? 'kit-radio-cards' : 'kit-radio-options'}>
        {options.map((option) => (
          <label key={option.value} className="kit-radio-choice">
            <input
              type="radio"
              name={name || id}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={option.disabled}
              required={required}
              aria-describedby={description}
            />
            <span className="kit-radio-copy">
              <strong>
                {option.jsxLabel ||
                  (needToTranslate ? t(option.label) : option.label)}
              </strong>
              {option.description && <small>{option.description}</small>}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p id={`${id}-error`} className="kit-radio-error" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
};

export default RadioGroup;
